import { ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import Auth from 'modules/Auth';
import { checkFeaturePermission } from 'utils/checkFeaturePermission';
import { useHasDealReadOnlyPermission } from 'customHooks/useHasDealReadOnlyPermission';
import { PermissionsEnum } from 'consts/permissions';
import Header from 'components/common/Header';
import SubNavigation from 'components/common/SubNavigation';
import { FeatureFlags } from 'components/common/types/Features.types';
import { useHasFeatureAccess } from 'customHooks/useHasFeatureAccess';
import Button, { Size, TextOverflow } from 'lib/Button';
import editIcon from 'assets/icons/edit.svg';
import blankIcon from 'assets/icons/blank.svg';
import { getIsReseller } from 'store/publisher/selectors';
import { Store } from 'components/common/types/Store.types';
import { NAVIGATION_PATHS } from 'consts/navigation';
import DealFormDropdown from './DealFormDropdown';

const DealsHeader: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const openPlannerNative = (): void => navigate('/planner/programmatic');
  const openPlannerDirectSales = (): void => navigate('/planner/direct-sales');
  const openProductCreationManager = (): void => navigate('/product');
  const openDealForm = (): void => navigate('/deal');

  const arePermissionsEnabled = useHasFeatureAccess(FeatureFlags.PLANNERS_PERMISSIONS);
  const hasDirectSalesPlannerEnabled = useHasFeatureAccess(FeatureFlags.DIRECT_SALES_PLANNER);
  const hasOnlyAllowPlanner = useHasFeatureAccess(FeatureFlags.ONLY_ALLOW_PLANNER);
  const hasDealReadOnlyPermission = useHasDealReadOnlyPermission();
  const hasVIOOHSelectFeatures = useSelector((state: Store) => state.reseller.isInternalPublisher);
  const isReseller = useSelector(getIsReseller);

  const createDealButton = (): ReactElement | null => {
    if (!Auth.hasPermission(PermissionsEnum.DEAL_CREATE) || hasDealReadOnlyPermission) {
      return null;
    }

    if (pathname.includes('product-management')) {
      return (
        <Button
          label="Create new product"
          size={Size.MEDIUM}
          svg={blankIcon}
          svgClassnames="mr-[4px]"
          onClick={openProductCreationManager}
          dataPendoId="/deals | Create new product"
        />
      );
    }
    if (pathname.includes('direct-sales')) {
      return (
        <Button
          label="Create campaign"
          size={Size.MEDIUM}
          svg={editIcon}
          svgClassnames="mr-[4px]"
          onClick={openPlannerDirectSales}
          textOverflow={TextOverflow.VISIBLE}
          dataPendoId="/deals | Create direct sales planner deal"
        />
      );
    }

    if (isReseller && !hasVIOOHSelectFeatures) {
      return (
        <Button
          label="Create campaign"
          size={Size.MEDIUM}
          svg={editIcon}
          svgClassnames="mr-[4px]"
          onClick={openPlannerNative}
          textOverflow={TextOverflow.VISIBLE}
          dataPendoId="/deals | Create reseller planner deal"
        />
      );
    }

    if (hasOnlyAllowPlanner) {
      return (
        <Button
          label="Create campaign"
          size={Size.MEDIUM}
          svg={editIcon}
          svgClassnames="mr-[4px]"
          onClick={openPlannerNative}
          textOverflow={TextOverflow.VISIBLE}
          dataPendoId="/planner/programmatic | Create planner deal"
        />
      );
    }

    return <DealFormDropdown openDealForm={openDealForm} openPlanner={openPlannerNative} />;
  };

  const dealsTabs = [
    {
      id: 1,
      path: NAVIGATION_PATHS.PROGRAMMATIC,
      isVisible: checkFeaturePermission(
        arePermissionsEnabled,
        Auth.hasPermission(PermissionsEnum.PROGRAMMATIC_PERMISSION),
      ),
      name: 'Programmatic',
    },
    {
      id: 2,
      path: NAVIGATION_PATHS.DIRECT_SALES,
      isVisible:
        hasDirectSalesPlannerEnabled &&
        checkFeaturePermission(arePermissionsEnabled, Auth.hasPermission(PermissionsEnum.DIRECT_SALES_PERMISSION)),
      name: 'Direct Sales',
    },
    {
      id: 3,
      path: NAVIGATION_PATHS.PRODUCT_MANAGEMENT,
      isVisible: hasDirectSalesPlannerEnabled && Auth.hasPermission(PermissionsEnum.PRODUCT_MANAGEMENT_PAGE_ACCESS),
      name: 'Products',
    },
  ];

  return (
    <Header
      title="Campaign Management"
      subNavigation={<SubNavigation tabsConfig={dealsTabs} />}
      actionButton={createDealButton()}
    />
  );
};

export default DealsHeader;
